/* ═══════════════════════════════════════════════════════════════
   Kellerbuch: Dreiwegezusammenführung

   Bisher galt: Haben Gerät und Server seit dem letzten Abgleich
   beide etwas geändert, entscheidet der Nutzer, welcher Stand
   gewinnt — der andere wird überschrieben (kern/sync.js). Das war
   ehrlich, aber grob: Wer offline eine Flasche entnimmt, während auf
   dem anderen Gerät ein neuer Wein angelegt wird, musste eines von
   beiden wegwerfen.

   Mit dem gemeinsamen Ausgangsstand (`basis` — der Stand, den dieses
   Gerät zuletzt mit dem Server bestätigt hat) lässt sich unterscheiden,
   WER WAS geändert hat. Dann gilt je Objekt und Feld:

     nur eine Seite geändert          → diese Seite
     beide gleich geändert            → übernehmen
     beide verschieden geändert       → Konflikt
     gelöscht hier, geändert dort     → Konflikt
     gelöscht auf einer Seite, sonst
     unverändert                      → gelöscht

   Bestände sind Zählerstände, keine Angaben: Trinkt man auf dem
   einen Gerät eine Flasche und auf dem anderen zwei, sind es drei
   weniger — nicht „die eine oder die andere Zahl“. Sie werden als
   Differenzen verrechnet (passend zu den Bewegungen, die beide Seiten
   ohnehin vollständig beitragen). Fiele ein Bestand dabei unter null,
   ist das ein echter Widerspruch und wird gemeldet.

   Ohne Basis (erster Abgleich nach dem Einführen, verlorener Speicher
   des Browsers) wird NICHT zusammengeführt — eine Löschung sähe dann
   aus wie eine Neuanlage auf der anderen Seite. Dann bleibt es bei
   der bisherigen Wahl, mit `unterschiede` als Hilfe.

   Reine Logik ohne I/O; Tests in tests/dreiwege.test.mjs.
   ═══════════════════════════════════════════════════════════════ */


/* Vergleich über eine stabile Zeichenkette — Schlüsselreihenfolge
   egal, `undefined` gleich `null`. */
function stabil(wert) {
  if (Array.isArray(wert)) return "[" + wert.map(stabil).join(",") + "]";
  if (wert && typeof wert === "object") {
    return "{" + Object.keys(wert).sort()
      .filter((k) => wert[k] !== undefined)
      .map((k) => JSON.stringify(k) + ":" + stabil(wert[k])).join(",") + "}";
  }
  return JSON.stringify(wert === undefined ? null : wert);
}
const gleich = (a, b) => stabil(a) === stabil(b);


/* Listen mit Kennung im Kellerbuch, und wie ihre Einträge heißen. */
const SAMMLUNGEN = {
  regions: { schluessel: "id" },
  winzer: { schluessel: "id" },
  weine: { schluessel: "id" },
  kritiken: { schluessel: "id" },
  entnahmen: { schluessel: "id" },
  bewegungen: { schluessel: "id" },
  weinkarten: { schluessel: "id" },
  katalogAbweichungen: { schluessel: "eintragId" },
};

/* Verschachtelte Listen mit Kennung — innerhalb eines Objekts. */
const UNTERLISTEN = { jahrgaenge: "id", eintraege: "id" };

/* Zwischenspeicher für abgerufene Daten: lassen sich jederzeit neu
   holen, Widersprüche sind dort bedeutungslos — der Server gewinnt,
   was nur eine Seite hat, bleibt. */
const ZWISCHENSPEICHER = new Set(["weatherCache", "klimaCache"]);

/* Gehört nicht zum Inhalt. */
const UEBERGEHEN = new Set(["version"]);


/**
 * Führt `lokal` und `server` auf Basis von `basis` zusammen.
 *
 * `beiKonflikt`: "lokal" oder "server" — welche Seite bei einem
 * Widerspruch in `db` steht. Die Liste `konflikte` ist davon
 * unabhängig und immer vollständig.
 *
 * Liefert { db, konflikte: [...], vonHier, vomServer }:
 *   konflikte  [{ sammlung, id, feld, art: "feld"|"geloescht"|"bestand",
 *                 lokal, server }]
 *   vonHier / vomServer  Zahl der Objekte, die jeweils nur eine Seite
 *              beigetragen hat (für die Rückmeldung).
 */
function fuehreDreiWegeZusammen(basis, lokal, server, { beiKonflikt = "lokal" } = {}) {
  const konflikte = [];
  const zaehler = { vonHier: 0, vomServer: 0 };
  const b = basis || {};
  const l = lokal || {};
  const s = server || {};
  const raus = {};

  const schluessel = new Set([...Object.keys(l), ...Object.keys(s)]);
  for (const k of schluessel) {
    if (UEBERGEHEN.has(k)) continue;
    if (SAMMLUNGEN[k]) {
      raus[k] = vereineListe(b[k] || [], l[k] || [], s[k] || [], SAMMLUNGEN[k].schluessel,
        { sammlung: k, konflikte, beiKonflikt, zaehler, oben: true });
    } else if (ZWISCHENSPEICHER.has(k)) {
      raus[k] = { ...(l[k] || {}), ...(s[k] || {}) };
    } else if (k === "katalogGesehen") {
      /* Bis wohin das Katalogprotokoll verarbeitet ist — die weitere
         Seite gewinnt, verarbeitet wird ohnehin idempotent. */
      const werte = [l[k], s[k]].filter((x) => typeof x === "number");
      raus[k] = werte.length ? Math.max(...werte) : (l[k] ?? s[k]);
    } else {
      raus[k] = vereineWert(b[k], l[k], s[k],
        { sammlung: k, id: null, feld: null, konflikte, beiKonflikt });
    }
  }
  if (typeof l.version === "number" || typeof s.version === "number") {
    raus.version = Math.max(l.version || 0, s.version || 0);
  }
  return { db: raus, konflikte, ...zaehler };
}


/* Ein einzelner Wert, ggf. ein verschachteltes Objekt. */
function vereineWert(b, l, s, ctx) {
  if (gleich(l, s)) return l;
  if (gleich(b, l)) return s;
  if (gleich(b, s)) return l;
  /* Beide verschieden geändert. Bei einfachen Objekten feldweise
     weiter, damit z. B. zwei verschiedene Einstellungen in `prefs`
     beide ankommen. */
  if (istObjekt(l) && istObjekt(s) && (b == null || istObjekt(b))) {
    return vereineObjekt(b || {}, l, s, ctx);
  }
  ctx.konflikte.push({
    sammlung: ctx.sammlung, id: ctx.id, feld: ctx.feld, art: "feld",
    lokal: l === undefined ? null : l, server: s === undefined ? null : s,
  });
  return ctx.beiKonflikt === "server" ? s : l;
}

const istObjekt = (x) => !!x && typeof x === "object" && !Array.isArray(x);


function vereineObjekt(b, l, s, ctx) {
  const raus = {};
  for (const f of new Set([...Object.keys(l), ...Object.keys(s)])) {
    const pfad = ctx.feld ? `${ctx.feld}.${f}` : f;
    if (UNTERLISTEN[f] && (Array.isArray(l[f]) || Array.isArray(s[f]))) {
      raus[f] = vereineListe(b[f] || [], l[f] || [], s[f] || [], UNTERLISTEN[f],
        { ...ctx, feld: pfad, oben: false });
      continue;
    }
    if (f === "bestaende" && (istObjekt(l[f]) || istObjekt(s[f]))) {
      raus[f] = vereineBestaende(b[f] || {}, l[f] || {}, s[f] || {}, { ...ctx, feld: pfad });
      continue;
    }
    const w = vereineWert(b[f], l[f], s[f], { ...ctx, feld: pfad });
    if (w !== undefined) raus[f] = w;
  }
  return raus;
}


/* Bestände je Flaschengröße als Differenzen. */
function vereineBestaende(b, l, s, ctx) {
  const raus = {};
  for (const g of new Set([...Object.keys(b), ...Object.keys(l), ...Object.keys(s)])) {
    const zb = Number(b[g]) || 0;
    const zl = Number(l[g]) || 0;
    const zs = Number(s[g]) || 0;
    const neu = zb + (zl - zb) + (zs - zb);
    if (neu < 0) {
      ctx.konflikte.push({ sammlung: ctx.sammlung, id: ctx.id, feld: `${ctx.feld}.${g}`, art: "bestand", lokal: zl, server: zs });
      const w = ctx.beiKonflikt === "server" ? zs : zl;
      if (w) raus[g] = w;
      continue;
    }
    /* Null bleibt stehen, wenn beide Seiten die Größe führen — so
       sieht ein unverändertes Feld nicht nach Änderung aus. */
    if (neu > 0 || (g in l && g in s)) raus[g] = neu;
  }
  return raus;
}


/* Eine Liste von Objekten mit Kennung. Reihenfolge: wie auf diesem
   Gerät, neue Einträge vom Server hinten angehängt. */
function vereineListe(b, l, s, schluessel, ctx) {
  const key = (o) => (o ? o[schluessel] : undefined);
  const mb = new Map(b.map((o) => [key(o), o]));
  const ml = new Map(l.map((o) => [key(o), o]));
  const ms = new Map(s.map((o) => [key(o), o]));
  const reihe = [...l.map(key), ...s.map(key).filter((id) => !ml.has(id))];
  const raus = [];

  for (const id of reihe) {
    const ob = mb.get(id);
    const ol = ml.get(id);
    const os = ms.get(id);
    const ictx = { ...ctx, id: ctx.oben ? id : ctx.id, feld: ctx.oben ? null : `${ctx.feld}[${id}]` };

    if (ol && os) {
      if (gleich(ol, os)) { raus.push(ol); continue; }
      if (ob && gleich(ob, ol)) { raus.push(os); if (ctx.oben) ctx.zaehler.vomServer++; continue; }
      if (ob && gleich(ob, os)) { raus.push(ol); if (ctx.oben) ctx.zaehler.vonHier++; continue; }
      raus.push(vereineObjekt(ob || {}, ol, os, ictx));
      continue;
    }

    if (ol && !os) {
      if (!ob) { raus.push(ol); if (ctx.oben) ctx.zaehler.vonHier++; continue; }   /* hier neu */
      if (gleich(ob, ol)) { if (ctx.oben) ctx.zaehler.vomServer++; continue; }      /* dort gelöscht */
      ctx.konflikte.push({ sammlung: ctx.sammlung, id: ictx.id, feld: ctx.oben ? null : ictx.feld,
        art: "geloescht", lokal: ol, server: null });
      if (ctx.beiKonflikt === "lokal") raus.push(ol);
      continue;
    }

    if (os && !ol) {
      if (!ob) { raus.push(os); if (ctx.oben) ctx.zaehler.vomServer++; continue; }  /* dort neu */
      if (gleich(ob, os)) { if (ctx.oben) ctx.zaehler.vonHier++; continue; }        /* hier gelöscht */
      ctx.konflikte.push({ sammlung: ctx.sammlung, id: ictx.id, feld: ctx.oben ? null : ictx.feld,
        art: "geloescht", lokal: null, server: os });
      if (ctx.beiKonflikt === "server") raus.push(os);
    }
  }
  return raus;
}


/**
 * Für den Fall ohne Basis: Was unterscheidet die beiden Stände
 * überhaupt? Je Sammlung die Objekte, die nur hier, nur auf dem
 * Server oder auf beiden verschieden vorliegen. Grundlage für die
 * Gegenüberstellung im Konfliktdialog.
 */
function unterschiedeKellerbuch(lokal, server) {
  const raus = [];
  for (const [sammlung, { schluessel }] of Object.entries(SAMMLUNGEN)) {
    if (sammlung === "katalogAbweichungen") continue;
    const ml = new Map(((lokal || {})[sammlung] || []).map((o) => [o[schluessel], o]));
    const ms = new Map(((server || {})[sammlung] || []).map((o) => [o[schluessel], o]));
    for (const [id, o] of ml) {
      if (!ms.has(id)) raus.push({ sammlung, id, art: "nurHier", lokal: o, server: null });
      else if (!gleich(o, ms.get(id))) raus.push({ sammlung, id, art: "verschieden", lokal: o, server: ms.get(id) });
    }
    for (const [id, o] of ms) {
      if (!ml.has(id)) raus.push({ sammlung, id, art: "nurServer", lokal: null, server: o });
    }
  }
  return raus;
}


/**
 * Ein lesbarer Name für einen Eintrag — für Konfliktliste und
 * Gegenüberstellung. Bewegungen, Entnahmen und Kritiken heißen nach
 * ihrem Wein.
 */
function eintragName(sammlung, eintrag, ...staende) {
  if (!eintrag) return "";
  if (eintrag.name) return eintrag.name;
  const weinId = eintrag.weinId;
  if (weinId) {
    for (const st of staende) {
      const w = ((st || {}).weine || []).find((x) => x.id === weinId);
      if (w) return w.name;
    }
  }
  return eintrag.weinText || "";
}


export { SAMMLUNGEN, eintragName, fuehreDreiWegeZusammen, gleich, stabil, unterschiedeKellerbuch };
